import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bot } from 'lucide-react';
import { exceptionsService, AppException } from '../../services/exceptionsService';
import { tradingApi } from '../../services/tradingApi';

function FixRecordLink({ fingerprint }: { fingerprint: string }) {
  const { data: fixes } = useQuery({
    queryKey: ['exception-fixes', fingerprint],
    queryFn: () => exceptionsService.getFixes(fingerprint),
  });
  const prUrl = fixes?.[0]?.pr_url;
  if (!prUrl) return null;
  return (
    <a
      href={prUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-xs text-green-400 hover:text-green-300 px-2 py-1 rounded border border-green-800"
    >
      View PR →
    </a>
  );
}

const STATUS_COLORS: Record<string, string> = {
  investigating: 'text-yellow-400',
  fixed: 'text-green-400',
  skipped: 'text-slate-400',
  invalid: 'text-slate-500',
};

const AGENT_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'unreviewed', label: 'Unreviewed' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'invalid', label: 'Invalid' },
];

export default function Exceptions() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [agentStatus, setAgentStatus] = useState('');
  const [minCount, setMinCount] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-exceptions', page, search, agentStatus, minCount],
    queryFn: () => exceptionsService.list({
      page,
      page_size: 25,
      exception_type: search || undefined,
      agent_status: agentStatus || undefined,
      min_count: minCount,
    }),
  });

  const { data: config } = useQuery({
    queryKey: ['trading-config'],
    queryFn: () => tradingApi.getConfig(),
  });

  const agentEnabled = config?.exception_agent_enabled ?? false;

  const toggleAgentMutation = useMutation({
    mutationFn: (enabled: boolean) => tradingApi.updateConfig({ exception_agent_enabled: enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trading-config'] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ fingerprint, updates }: { fingerprint: string; updates: any }) =>
      exceptionsService.update(fingerprint, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-exceptions'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (fingerprint: string) => exceptionsService.delete(fingerprint),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-exceptions'] }),
  });

  const totalPages = data ? Math.ceil(data.total / 25) : 1;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Application Exceptions</h1>
          <p className="text-slate-400 text-sm mt-1">
            {data?.total ?? 0} unique exception fingerprints recorded
          </p>
        </div>

        {/* Exception Fix Agent toggle */}
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
          agentEnabled
            ? 'bg-purple-500/10 border-purple-500/30'
            : 'bg-slate-800 border-slate-700'
        }`}>
          <Bot size={18} className={agentEnabled ? 'text-purple-400' : 'text-slate-500'} />
          <div>
            <p className={`text-sm font-medium ${agentEnabled ? 'text-purple-300' : 'text-slate-300'}`}>
              Fix Agent
            </p>
            <p className="text-xs text-slate-500">Auto-creates draft PRs for unreviewed exceptions</p>
          </div>
          <button
            onClick={() => toggleAgentMutation.mutate(!agentEnabled)}
            disabled={toggleAgentMutation.isPending}
            className={`relative w-10 h-6 rounded-full transition-colors duration-200 focus:outline-none ${
              agentEnabled ? 'bg-purple-500' : 'bg-slate-600'
            } disabled:opacity-50`}
            title={agentEnabled ? 'Disable fix agent' : 'Enable fix agent'}
          >
            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
              agentEnabled ? 'translate-x-5' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <input
          className="bg-slate-800 text-white border border-slate-600 rounded px-3 py-1.5 text-sm w-64"
          placeholder="Filter by exception type..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className="bg-slate-800 text-white border border-slate-600 rounded px-3 py-1.5 text-sm"
          value={agentStatus}
          onChange={e => { setAgentStatus(e.target.value); setPage(1); }}
        >
          {AGENT_STATUS_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-400">
          Min count:
          <input
            type="number"
            min={1}
            className="bg-slate-800 text-white border border-slate-600 rounded px-2 py-1.5 w-20 text-sm"
            value={minCount}
            onChange={e => { setMinCount(Number(e.target.value) || 1); setPage(1); }}
          />
        </label>
      </div>

      {/* Table */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-400">Loading...</div>
        ) : !data?.items.length ? (
          <div className="p-8 text-center text-slate-400">No exceptions found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Exception</th>
                <th className="px-4 py-3 text-right">Count</th>
                <th className="px-4 py-3 text-left">Last Seen</th>
                <th className="px-4 py-3 text-left">Endpoint</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {data.items.map(exc => (
                <React.Fragment key={exc.fingerprint}>
                  <tr
                    className="hover:bg-slate-700/50 cursor-pointer"
                    onClick={() => setExpanded(expanded === exc.fingerprint ? null : exc.fingerprint)}
                  >
                    <td className="px-4 py-3">
                      <div className="text-red-400 font-mono text-xs">{exc.exception_type}</div>
                      <div className="text-slate-400 text-xs truncate max-w-xs">{exc.exception_message}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${exc.count >= 10 ? 'text-red-400' : exc.count >= 3 ? 'text-yellow-400' : 'text-slate-300'}`}>
                        {exc.count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {new Date(exc.last_seen_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">
                      {exc.method && <span className="text-blue-400 mr-1">{exc.method}</span>}
                      {exc.endpoint}
                    </td>
                    <td className="px-4 py-3">
                      {exc.is_system_exception ? (
                        <span className="text-slate-500 text-xs">system</span>
                      ) : exc.agent_status ? (
                        <span className={`text-xs ${STATUS_COLORS[exc.agent_status] || 'text-slate-400'}`}>
                          {exc.agent_status}
                        </span>
                      ) : (
                        <span className="text-orange-400 text-xs">unreviewed</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2" onClick={e => e.stopPropagation()}>
                        <button
                          className="text-xs text-slate-400 hover:text-slate-300 px-2 py-1 rounded border border-slate-600"
                          onClick={() => updateMutation.mutate({ fingerprint: exc.fingerprint, updates: { is_system_exception: !exc.is_system_exception } })}
                        >
                          {exc.is_system_exception ? 'Unmark System' : 'Mark System'}
                        </button>
                        <button
                          className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded border border-red-900"
                          onClick={() => { if (confirm('Delete this exception record?')) deleteMutation.mutate(exc.fingerprint); }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded === exc.fingerprint && (
                    <tr>
                      <td colSpan={6} className="px-4 py-3 bg-slate-900">
                        <div className="text-xs text-slate-400 mb-2">
                          Fingerprint: <code className="text-slate-300">{exc.fingerprint}</code>
                          {' · '}First seen: {new Date(exc.first_seen_at).toLocaleString()}
                          {exc.user_id && ` · User ID: ${exc.user_id}`}
                        </div>
                        <pre className="text-xs text-slate-300 bg-slate-950 rounded p-3 overflow-auto max-h-64 font-mono whitespace-pre-wrap">
                          {exc.traceback || 'No traceback available'}
                        </pre>
                        <div className="mt-2 flex gap-2">
                          {!exc.agent_status && (
                            <button
                              className="text-xs text-slate-400 hover:text-slate-300 px-2 py-1 rounded border border-slate-600"
                              onClick={() => updateMutation.mutate({ fingerprint: exc.fingerprint, updates: { agent_status: 'skipped' } })}
                            >
                              Mark Skipped
                            </button>
                          )}
                          {exc.agent_status && exc.agent_status !== 'fixed' && (
                            <button
                              className="text-xs text-slate-400 hover:text-slate-300 px-2 py-1 rounded border border-slate-600"
                              onClick={() => updateMutation.mutate({ fingerprint: exc.fingerprint, updates: { agent_status: null } })}
                            >
                              Reset Status
                            </button>
                          )}
                          {exc.agent_status === 'fixed' && (
                            <FixRecordLink fingerprint={exc.fingerprint} />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <button
            className="px-3 py-1 text-sm rounded border border-slate-600 text-slate-400 disabled:opacity-50"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </button>
          <span className="px-3 py-1 text-sm text-slate-400">
            {page} / {totalPages}
          </span>
          <button
            className="px-3 py-1 text-sm rounded border border-slate-600 text-slate-400 disabled:opacity-50"
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
